import type { AiUsageRange, AiUsageSummary } from '@owl/shared';
import { useCallback, useState } from 'react';
import type { ShellCtx } from '../components/Shell';
import {
  ChartsRow,
  Kpi,
  formatInt,
  formatSeconds,
  formatUsd,
} from '../components/charts/AiUsageBlocks';
import { api } from '../lib/api';
import { useLoader } from '../lib/useLoader';

const RANGES: AiUsageRange[] = ['1h', '6h', '24h', '7d', '30d', 'mtd'];

export function AiUsage({ ctx }: { ctx: ShellCtx }) {
  const refreshTick = ctx.refreshTick;
  const env = ctx.environment;
  const [range, setRange] = useState<AiUsageRange>('24h');
  const [summary, setSummary] = useState<AiUsageSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const s = await api.aiUsageSummary({ range, environment: env });
      setSummary(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  }, [env, range]);

  useLoader(load, refreshTick);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="view-title" style={{ margin: 0 }}>
            AI Dashboard
          </h1>
          <div className="view-sub">Token consumption and cost across all registered APIs</div>
        </div>
        <div className="range-picker">
          {RANGES.map((r) => (
            <button
              type="button"
              key={r}
              className={`range-opt ${range === r ? 'active' : ''}`}
              onClick={() => setRange(r)}
            >
              {r === 'mtd' ? 'MTD' : r}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <SummaryPanel summary={summary} range={range} ctx={ctx} />
    </div>
  );
}

function SummaryPanel({
  summary,
  range,
  ctx,
}: {
  summary: AiUsageSummary | null;
  range: AiUsageRange;
  ctx: ShellCtx;
}) {
  if (summary === null) {
    return <div className="empty-state">Loading…</div>;
  }
  const t = summary.totals;
  const totalCost = t.costUsd;
  const showAudio = t.audioSeconds > 0 || (summary.allTime?.audioSeconds ?? 0) > 0;
  const showCharacters = t.characters > 0 || (summary.allTime?.characters ?? 0) > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {summary.allTime && (
        <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
          <Kpi label="Events · all-time" value={formatInt(summary.allTime.events)} />
          <Kpi label="Input tokens · all-time" value={formatInt(summary.allTime.inputTokens)} />
          {showAudio && (
            <Kpi label="Audio · all-time" value={formatSeconds(summary.allTime.audioSeconds)} />
          )}
          {showCharacters && (
            <Kpi label="Characters · all-time" value={formatInt(summary.allTime.characters)} />
          )}
          <Kpi label="Cost · all-time" value={formatUsd(summary.allTime.costUsd)} />
        </div>
      )}

      <div className="kpi-row">
        <Kpi label="Events" value={formatInt(t.events)} />
        <Kpi
          label="Input tokens"
          value={formatInt(t.inputTokens)}
          delta={t.cachedInputTokens > 0 ? `${formatInt(t.cachedInputTokens)} cached` : undefined}
        />
        <Kpi label="Output tokens" value={formatInt(t.outputTokens)} />
        {showAudio && <Kpi label="Audio processed" value={formatSeconds(t.audioSeconds)} />}
        {showCharacters && <Kpi label="Characters" value={formatInt(t.characters)} />}
        <Kpi label="Cost (USD)" value={formatUsd(t.costUsd)} />
      </div>

      <ChartsRow summary={summary} range={range} />

      <div className="dual-tables">
        <div className="table-wrap">
          <div className="view-sub" style={{ padding: '6px 4px 8px' }}>
            By application
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Application</th>
                <th>Events</th>
                <th>Input</th>
                <th>Output</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              {summary.byApplication.length === 0 ? (
                <tr>
                  <td colSpan={5} className="dim" style={{ padding: 16, textAlign: 'center' }}>
                    No events in this range
                  </td>
                </tr>
              ) : (
                summary.byApplication.map((a) => (
                  <tr
                    key={a.applicationId}
                    onClick={() =>
                      ctx.navigate({ name: 'aiappdetail', applicationId: a.applicationId })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        ctx.navigate({ name: 'aiappdetail', applicationId: a.applicationId });
                      }
                    }}
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    title="Open AI app detail"
                  >
                    <td>
                      <strong>{a.applicationName}</strong>
                    </td>
                    <td className="mono">{formatInt(a.events)}</td>
                    <td className="mono">{formatInt(a.inputTokens)}</td>
                    <td className="mono">{formatInt(a.outputTokens)}</td>
                    <td className="mono">{formatUsd(a.costUsd)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="table-wrap">
          <div className="view-sub" style={{ padding: '6px 4px 8px' }}>
            By model
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Provider / Model</th>
                <th>Events</th>
                <th>Input</th>
                <th>Output</th>
                <th>Cost</th>
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              {summary.byModel.length === 0 ? (
                <tr>
                  <td colSpan={6} className="dim" style={{ padding: 16, textAlign: 'center' }}>
                    No events in this range
                  </td>
                </tr>
              ) : (
                summary.byModel.map((m) => (
                  <tr key={`${m.provider}/${m.model}`}>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: 3,
                          background: m.color,
                          marginRight: 8,
                          verticalAlign: 'middle',
                        }}
                      />
                      <strong>{m.displayLabel}</strong>{' '}
                      <span className="dim" style={{ fontSize: 11 }}>
                        ({m.provider}/{m.model})
                      </span>
                    </td>
                    <td className="mono">{formatInt(m.events)}</td>
                    <td className="mono">{formatInt(m.inputTokens)}</td>
                    <td className="mono">{formatInt(m.outputTokens)}</td>
                    <td className="mono">{formatUsd(m.costUsd)}</td>
                    <td className="mono dim">
                      {totalCost > 0 ? `${((m.costUsd / totalCost) * 100).toFixed(1)}%` : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
